import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import SearchIcon from "@material-ui/icons/Search";
import TuneIcon from "@material-ui/icons/Tune";
import AddIcon from "@material-ui/icons/Add";

import { AuthContext } from "../../../context/Auth/AuthContext";
import { TicketsContext } from "../../../context/Tickets/TicketsContext";
import useTickets from "../../../hooks/useTickets";
import NewTicketModal from "../../../components/NewTicketModal";
import TicketListItemCustom from "../../../components/TicketListItemCustom";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const STATUS_TABS = [
  { key: "open", label: "Abertos" },
  { key: "pending", label: "Aguardando" },
  { key: "closed", label: "Encerrados" },
];

const TicketsListPanel = ({ tokens, selectedUuid, fullWidth }) => {
  const { user, socket } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);

  const [activeTab, setActiveTab] = useState("open");
  const [searchParam, setSearchParam] = useState("");
  const [quickFilter, setQuickFilter] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  const myQueueIds = useMemo(() => (user.queues || []).map((q) => q.id), [user.queues]);
  const today = format(new Date(), "yyyy-MM-dd");

  // "Minha fila" e' uma escolha explicita do usuario de estreitar pros
  // proprios setores - nesse caso showAll fica false de proposito, pra
  // nao ser alargado de volta pela regra de admin logo abaixo.
  const showAllTickets = user.profile === "admin" || user.allUserChat === "enabled";
  const effectiveShowAll = quickFilter === "minhaFila" ? "false" : showAllTickets ? "true" : "false";

  // Fix 2026-10-23 (reportado pelo usuario: "aguardando nao mostrou
  // notificacao de cliente aguardando e nem quantos"): useTickets so'
  // busca contagem UMA VEZ, no mount - nao tem nenhum listener de
  // socket, nem aqui nem no Classico (TicketsManagerTabs, mesmo
  // problema, nao mexido ainda). O numero de "Aguardando" ficava parado
  // ate' a tela ser recarregada. "forceSearch" e' um parametro que o
  // proprio useTickets ja aceita e coloca no array de dependencias do
  // efeito, mas nunca usa pra montar a query - serve exatamente pra
  // forcar um refetch sem inventar nada novo no hook compartilhado.
  // Bumping ele a cada evento relevante de socket (ticket/appMessage)
  // faz as contagens se atualizarem ao vivo.
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshTimerRef = useRef(null);
  useEffect(() => {
    if (!socket || !user?.companyId) return undefined;
    const companyId = user.companyId;
    const scheduleRefresh = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        setRefreshTick((prev) => prev + 1);
      }, 400);
    };
    socket.on(`company-${companyId}-ticket`, scheduleRefresh);
    socket.on(`company-${companyId}-appMessage`, scheduleRefresh);
    return () => {
      socket.off(`company-${companyId}-ticket`, scheduleRefresh);
      socket.off(`company-${companyId}-appMessage`, scheduleRefresh);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [socket, user?.companyId]);

  const { tickets } = useTickets({
    searchParam,
    status: activeTab,
    showAll: effectiveShowAll,
    queueIds: quickFilter === "minhaFila" ? JSON.stringify(myQueueIds) : undefined,
    withUnreadMessages: quickFilter === "naoLidos" ? "true" : undefined,
    dateStart: quickFilter === "hoje" ? today : undefined,
    dateEnd: quickFilter === "hoje" ? today : undefined,
    forceSearch: refreshTick,
  });
  const { count: openCount } = useTickets({ status: "open", showAll: showAllTickets ? "true" : "false", forceSearch: refreshTick });
  const { count: pendingCount } = useTickets({ status: "pending", showAll: showAllTickets ? "true" : "false", forceSearch: refreshTick });

  // Pisca o selo de "Aguardando" por alguns segundos quando o numero
  // sobe - pedido explicito do usuario ("preciso que ele pisque quando
  // chegar algum novo"). So' pisca em alta (numero aumentou), nunca
  // quando so' diminui (ticket sendo aceito por outro atendente).
  const previousPendingCountRef = useRef(pendingCount);
  const [pendingBlinking, setPendingBlinking] = useState(false);
  useEffect(() => {
    if (pendingCount > previousPendingCountRef.current) {
      setPendingBlinking(true);
      const timer = setTimeout(() => setPendingBlinking(false), 4000);
      previousPendingCountRef.current = pendingCount;
      return () => clearTimeout(timer);
    }
    previousPendingCountRef.current = pendingCount;
    return undefined;
  }, [pendingCount]);

  const grouped = useMemo(() => {
    const groups = {};
    (tickets || []).forEach((t) => {
      const key = t.queue?.name || "SEM FILA";
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }, [tickets]);

  const handleSelect = (ticket) => {
    setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
  };

  const handleCloseAll = async () => {
    try {
      await Promise.all(
        (tickets || [])
          .filter((t) => t.status === "open")
          .map((t) => api.put(`/tickets/${t.id}`, { status: "closed", userId: user.id }))
      );
    } catch (err) {
      toastError(err);
    }
  };

  const chip = (key, label) => (
    <button
      key={key}
      onClick={() => setQuickFilter((prev) => (prev === key ? null : key))}
      style={{
        height: 30,
        padding: "0 12px",
        borderRadius: 15,
        border: `1px solid ${quickFilter === key ? tokens.primary : tokens.borderInput}`,
        background: quickFilter === key ? tokens.primarySoft : tokens.surface,
        color: quickFilter === key ? tokens.primaryText : tokens.text2,
        fontSize: 13,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <section
      aria-label="Tickets"
      style={{
        flex: fullWidth ? "1 1 auto" : "0 1 380px",
        minWidth: fullWidth ? 0 : 300,
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 16,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes voicechat-pending-blink {
          0%, 100% { background-color: ${tokens.dividerStrong}; color: ${tokens.textMuted}; }
          50% { background-color: ${tokens.primary}; color: #FFFFFF; }
        }
        .voicechat-pending-badge-blink {
          animation: voicechat-pending-blink 1s ease-in-out 4;
        }
      `}</style>
      <div style={{ padding: "16px 16px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <SearchIcon style={{ position: "absolute", left: 12, top: 11, color: tokens.textMuted2, fontSize: 18 }} />
            <input
              type="search"
              placeholder="Buscar contato ou mensagem…"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                height: 40,
                padding: "0 12px 0 38px",
                border: `1px solid ${tokens.borderInput}`,
                borderRadius: 10,
                background: tokens.surfaceSoft,
                fontSize: 14,
                color: tokens.text,
              }}
            />
          </div>
          <button
            aria-label="Filtros"
            title="Filtros"
            style={{ width: 40, height: 40, border: `1px solid ${tokens.borderInput}`, background: tokens.surface, borderRadius: 10, color: tokens.neutralChipText, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <TuneIcon fontSize="small" />
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {chip("hoje", "Hoje")}
          {chip("naoLidos", "Não lidos")}
          {chip("minhaFila", "Minha fila")}
        </div>
      </div>

      <div role="tablist" style={{ display: "flex", borderBottom: `1px solid ${tokens.divider}`, padding: "0 8px" }}>
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const tabCount = tab.key === "open" ? openCount : tab.key === "pending" ? pendingCount : null;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                height: 44,
                border: "none",
                background: "transparent",
                borderBottom: isActive ? `2px solid ${tokens.primary}` : "2px solid transparent",
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? tokens.primaryDark : tokens.textMuted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              {tab.label}
              {tabCount !== null && (
                <span
                  className={tab.key === "pending" && pendingBlinking ? "voicechat-pending-badge-blink" : undefined}
                  style={{ padding: "1px 7px", borderRadius: 10, background: isActive ? tokens.primarySoft : tokens.dividerStrong, color: isActive ? tokens.primaryText : tokens.textMuted, fontSize: 12 }}
                >
                  {tabCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8, padding: "12px 16px" }}>
        <button
          onClick={() => setNewTicketOpen(true)}
          style={{ flex: 1, height: 38, border: "none", borderRadius: 10, background: tokens.primary, color: "#FFFFFF", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer" }}
        >
          <AddIcon fontSize="small" /> Novo ticket
        </button>
        {activeTab === "open" && (
          <button
            onClick={handleCloseAll}
            style={{ height: 38, padding: "0 14px", border: `1px solid ${tokens.borderInput}`, borderRadius: 10, background: tokens.surface, color: tokens.text2, fontSize: 14, fontWeight: 500, cursor: "pointer" }}
          >
            Fechar todos
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 12px", display: "flex", flexDirection: "column", gap: 16 }}>
        {Object.keys(grouped).length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: tokens.textMuted, fontSize: 13 }}>
            Nenhum atendimento encontrado
          </div>
        )}
        {Object.entries(grouped).map(([queueName, queueTickets]) => (
          <div key={queueName}>
            <div style={{ padding: "4px 8px 6px", fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: tokens.textMuted2 }}>
              {queueName} · {queueTickets.length}
            </div>
            {/* Fix urgente 2026-10-22: o card desenhado aqui na mao (antes
                deste commit) nao tinha os botoes de aceitar/transferir/
                ignorar que o Classico sempre teve (TicketListItemCustom) -
                atendente nao conseguia mais agir num ticket direto da
                lista, so' abrindo a conversa. Reportado pelo usuario como
                "clientes parados na fila e ninguem sabe" - prioridade
                maxima, revertido pra reusar o componente real do Classico
                (com todos os modais/handlers de aceitar, transferir,
                ignorar, reabrir) em vez do card customizado. Perde o
                visual proprio do mockup por enquanto, funcionalidade
                primeiro. selectedUuid nao e' mais usado pra destacar a
                linha selecionada (TicketListItemCustom nao aceita essa
                prop) - fica pra uma proxima rodada de polish. */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              {queueTickets.map((ticket) => (
                <TicketListItemCustom key={ticket.id} ticket={ticket} setTabOpen={setActiveTab} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <NewTicketModal
        modalOpen={newTicketOpen}
        onClose={(ticket) => {
          setNewTicketOpen(false);
          if (ticket) handleSelect(ticket);
        }}
      />
    </section>
  );
};

export default TicketsListPanel;
